import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getEconomicsQuestions, submitEconomicsAnswers } from '../../api';
import './EconomicsQuiz.css';

const EconomicsQuiz = ({ moduleSlug, moduleTitle }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [error, setError] = useState(null);
  
  // Refs für die Fragen-Karten
  const questionRefs = useRef({});

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🏢 Lade Wirtschaftsfragen für Modul:', moduleSlug);
      
      const response = await getEconomicsQuestions(moduleSlug);
      console.log('📥 Wirtschafts-API Response:', response.data);
      
      setQuestions(response.data.questions);
      setSessionId(response.data.sessionId);
      
      if (response.data.questions.length === 0) {
        setError('Keine Wirtschaftsfragen für dieses Modul gefunden.');
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der Wirtschaftsfragen:', error);
      setError('Fehler beim Laden der Fragen: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [moduleSlug]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Hilfsfunktion: Gibt die verfügbaren Optionen für eine Frage zurück
  const getAvailableOptions = (question) => {
    return ['A', 'B', 'C', 'D', 'E'].filter(
      (opt) => question[`option_${opt.toLowerCase()}`]
    );
  };

  // Angepasst für single_select und multi_select
  const handleAnswerChange = (questionId, selectedAnswer, isMulti) => {
    setAnswers(prev => {
      if (!isMulti) {
        return { ...prev, [questionId]: selectedAnswer };
      } else {
        const prevArr = Array.isArray(prev[questionId]) ? prev[questionId] : [];
        let newArr;
        if (prevArr.includes(selectedAnswer)) {
          newArr = prevArr.filter((a) => a !== selectedAnswer);
        } else {
          newArr = [...prevArr, selectedAnswer];
        }
        return { ...prev, [questionId]: newArr };
      }
    });
    setUnansweredQuestions((prev) => prev.filter((id) => id !== questionId));
  };

  const scrollToFirstUnanswered = () => {
    if (unansweredQuestions.length > 0) {
      const firstUnansweredId = unansweredQuestions[0];
      const element = questionRefs.current[firstUnansweredId];
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setTimeout(() => {
          element.focus();
        }, 500);
      }
    }
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter(q => {
      if (q.question_type === 'multi_select') {
        return !answers[q.id] || answers[q.id].length === 0;
      } else {
        return !answers[q.id];
      }
    }).map(q => q.id);
    
    if (unanswered.length > 0) {
      setUnansweredQuestions(unanswered);
      setTimeout(scrollToFirstUnanswered, 100);
      return;
    }

    try {
      setSubmitting(true);
      const answersArray = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        questionId: parseInt(questionId),
        selectedAnswer
      }));

      const response = await submitEconomicsAnswers({
        sessionId,
        answers: answersArray
      });

      setResults(response.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('❌ Fehler bei Wirtschafts-Auswertung:', error);
      setError('Fehler beim Auswerten: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setResults(null);
    setUnansweredQuestions([]);
    setError(null);
    questionRefs.current = {};
    loadQuestions();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hilfsfunktion um die Antwort-Option als Text zu bekommen
  const getAnswerText = (question, option) => {
    if (!option || typeof option !== 'string') return '';
    const optionKey = `option_${option.toLowerCase()}`;
    return question[optionKey];
  };

  if (loading) {
    return <div className="economics-quiz-loading">Lade Wirtschaftsfragen...</div>;
  }

  if (error) {
    return (
      <div className="economics-quiz-error">
        <h3>Fehler</h3>
        <p>{error}</p>
        <button onClick={loadQuestions} className="retry-button">
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (results) {
    return (
      <div className="economics-quiz-results">
        <h3>Wirtschafts-Quiz abgeschlossen!</h3>
        <div className="results-summary">
          <p>Richtige Antworten: {results.summary.correctAnswers} von {results.summary.totalQuestions}</p>
          <p>Ergebnis: {results.summary.scorePercentage}%</p>
          <div className={`score-badge ${results.summary.scorePercentage >= 70 ? 'passed' : 'failed'}`}>
            {results.summary.scorePercentage >= 70 ? 'Bestanden' : 'Nicht bestanden'}
          </div>
        </div>
        <div className="detailed-results">
          <h4>Detaillierte Ergebnisse:</h4>
          {results.results.map((result, index) => {
            // Finde die entsprechende Frage
            const question = results.questionsData?.find(q => String(q.id) === String(result.questionId)) || 
                           questions.find(q => String(q.id) === String(result.questionId));
            const isMulti = question?.question_type === 'multi_select';
            return (
              <div key={result.questionId} className={`result-item ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                <div className="result-content">
                  <div className="result-header">
                    <h5>Frage {index + 1}</h5>
                    <span className={`result-icon ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                      {result.isCorrect ? '✓' : '✗'}
                    </span>
                  </div>
                  {question && (
                    <div className="result-details">
                      <p className="question-text">{question.question_text}</p>
                      <p className="user-answer">
                        <strong>Ihre Antwort:</strong> 
                        <span className={result.isCorrect ? 'correct-answer' : 'wrong-answer'}>
                          {isMulti
                            ? Array.isArray(result.selectedAnswer)
                              ? result.selectedAnswer.map(opt => `${opt}: ${getAnswerText(question, opt)}`).join(', ')
                              : ''
                            : `${result.selectedAnswer}: ${getAnswerText(question, result.selectedAnswer)}`}
                        </span>
                      </p>
                      {!result.isCorrect && (
                        <p className="correct-answer-display">
                          <strong>Richtige Antwort:</strong> 
                          <span className="correct-answer">
                            {isMulti
                              ? Array.isArray(result.correctAnswer)
                                ? result.correctAnswer.map(opt => `${opt}: ${getAnswerText(question, opt)}`).join(', ')
                                : ''
                              : `${result.correctAnswer}: ${getAnswerText(question, result.correctAnswer)}`}
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={resetQuiz} className="retry-button">
          Quiz wiederholen
        </button>
      </div>
    );
  }

  return (
    <div className="economics-quiz">
      <div className="quiz-header">
        <h3>{moduleTitle} - Wirtschafts-Quiz</h3>
        <p>{questions.length} Fragen</p>
      </div>

      <div className="questions-container">
        {questions.map((question, index) => {
          const isMulti = question.question_type === 'multi_select';
          const availableOptions = getAvailableOptions(question);
          
          return (
            <div 
              key={question.id}
              ref={el => questionRefs.current[question.id] = el}
              className={`question-card ${unansweredQuestions.includes(question.id) ? 'unanswered' : ''}`}
              tabIndex={-1}
            >
              <div className="question-header">
                <h4>Frage {index + 1}</h4>
                {question.category && (
                  <span className="question-category">{question.category}</span>
                )}
                {unansweredQuestions.includes(question.id) && (
                  <span className="unanswered-warning">Bitte beantworten!</span>
                )}
              </div>
              
              <p className="question-text">{question.question_text}</p>
              
              <div className="options-container">
                {availableOptions.map((option) => {
                  const optionText = question[`option_${option.toLowerCase()}`];
                  const isSelected = isMulti 
                    ? Array.isArray(answers[question.id]) && answers[question.id].includes(option)
                    : answers[question.id] === option;
                  
                  return (
                    <label key={option} className="option-label">
                      <input
                        type={isMulti ? "checkbox" : "radio"}
                        name={`question-${question.id}`}
                        value={option}
                        checked={isSelected}
                        onChange={() => handleAnswerChange(question.id, option, isMulti)}
                      />
                      <span className="option-text">
                        <strong>{option}:</strong> {optionText}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="quiz-footer">
        <div className="progress-info">
          Beantwortete Fragen: {Object.keys(answers).length} von {questions.length}
        </div>
        <button 
          onClick={handleSubmit} 
          disabled={submitting || Object.keys(answers).length === 0}
          className="submit-button"
        >
          {submitting ? 'Wird ausgewertet...' : 'Quiz abschließen'}
        </button>
      </div>
    </div>
  );
};

export default EconomicsQuiz; 